import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { IonPage, useIonAlert, useIonViewDidEnter } from '@ionic/react';
import { useIsKeyBoardShown } from 'src/hooks/useIsKeyboardShown';
import {
  Container_v2 as Container,
  AppHeader_v2 as AppHeader,
  Button_v2 as Button,
  AppFooter_v2 as AppFooter,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
  IconArrowLeft,
} from '@mindly/ui-components';
import useToast from 'src/context/ToastContext/ToastContext';
import { userSelector } from 'src/redux/user/selector';
import { setUser } from 'src/redux/user/reducer';
import { validateEmail } from 'src/shared/utils';
import { getIsExistsSpecialist } from 'src/api/user';
import { useUpdateProfile } from 'src/shared/hooks/useUpdateProfile';
import { useAuthFlow } from 'src/shared/hooks/useAuthFlow';

const ProfileEmail: React.FC = () => {
  const { t: profileT } = useTranslation('profile');
  const { t: appT } = useTranslation('app');
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [present] = useIonAlert();
  const history = useHistory();
  const { successfullyUpdatedProfile } = useUpdateProfile();
  const isKeyboardShown = useIsKeyBoardShown();
  const user = useSelector(userSelector);

  const { updateEmail } = useAuthFlow();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [specialistExistsLoading, setSpecialistExistsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(user?.email ?? '');

  useIonViewDidEnter(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  const onSubmitAction = () => {
    if (inputValue) {
      setIsLoading(true);
      updateEmail({ newEmail: inputValue }).then(
        ({ error, message, data }) => {
          setIsLoading(false);

          if (error) {
            console.warn('Error updating user email', error);
            showToast({
              message:
                profileT(message) !== message ? profileT(message) : appT(message) !== message ? appT(message) : message,
            });
            return;
          }

          dispatch(setUser(data));
          successfullyUpdatedProfile(true, true);
        },
        () => setIsLoading(false)
      );
    }
  };

  const showSpecialistExistsWarning = () => {
    present({
      mode: 'ios',
      header: profileT('personal_details.edit_email_confirmation.title'),
      subHeader: profileT('personal_details.edit_email_confirmation.description'),
      buttons: [
        {
          text: profileT('personal_details.edit_email_confirmation.confirm_button'),
          cssClass: 'confirm-delete-button',
          handler: onSubmitAction,
        },
        {
          text: profileT('personal_details.edit_email_confirmation.cancel_button'),
        },
      ],
    });
  };

  const runUpdateEmailProcess = async () => {
    if (user?.id && !specialistExistsLoading) {
      if (!validateEmail(inputValue)) {
        showToast({ message: profileT('personal_details.errors.invalid_email') });
        return;
      }

      setSpecialistExistsLoading(true);
      try {
        await getIsExistsSpecialist(user?.id).then(
          ({ data, error, message }) => {
            setSpecialistExistsLoading(false);

            if (error) {
              showToast({
                message: message ? appT(message) : 'Something went wrong',
              });
              return;
            }

            if (data) {
              showSpecialistExistsWarning();
            } else {
              onSubmitAction();
            }
          },
          () => {
            setSpecialistExistsLoading(false);
          }
        );
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  return (
    <IonPage>
      <AppHeader className="flex flex-row items-start mb-4 cursor-pointer">
        <IconArrowLeft onClick={() => history.back()} />
      </AppHeader>
      <Container isFlexContainer>
        <div className={'flex flex-col gap-1'}>
          <Typography variant={TypographyVariantsEnum.Title28Semi}>
            {profileT('personal_details.edit_email.title')}
          </Typography>
        </div>
        <ScreenInput
          ref={inputRef}
          type={'email'}
          name={'client-name'}
          placeholder={profileT('personal_details.edit_email.title')}
          value={inputValue}
          onChange={(e: any) => setInputValue(e.target.value)}
          onKeyDown={(e: any) => e.key === 'Enter' && runUpdateEmailProcess()}
          required
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button
          isLoading={isLoading}
          disabled={!inputValue || isLoading || specialistExistsLoading || inputValue === user?.email}
          fullWidth
          size={'large'}
          onClick={() => runUpdateEmailProcess()}
        >
          {profileT('save_button')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default ProfileEmail;
